import { Fragment } from "react";
import { useT } from "../i18n/hooks.ts";
import type { TextKey } from "../i18n/en.ts";
import { DataIcon } from "./DataIcon.tsx";
import { DATA_TAB_ICONS } from "./dataTabIcons.tsx";
import type { DataTab } from "./ProjectDataDialog.tsx";
import { DropdownMenu, DropdownMenuItem } from "./DropdownMenu.tsx";

/**
 * Audit finding: this menu used to render its 14 tab items as one flat
 * list, which read as an undifferentiated wall of icons+labels. Group
 * them by SWMM domain instead, with the same small `.menu-popover-group-
 * label` header the "Add element" menu already uses for NODES/LINKS/
 * HYDROLOGY. Five groups (the audit named four — Hidrología/Hidráulica/
 * Calidad/Control — but Curves/Time series/Time patterns are shared
 * data-type building blocks used by ALL of them, so they get their own
 * upfront group instead of being force-fit into one of the four). Items
 * within each group keep the ProjectDataDialog.tsx tab order they
 * already had.
 */
const GROUPS: { labelKey: TextKey; items: { key: DataTab; labelKey: TextKey }[] }[] = [
  {
    labelKey: "projectData.groupData",
    items: [
      { key: "curves", labelKey: "projectData.tabCurves" },
      { key: "timeseries", labelKey: "projectData.tabTimeSeries" },
      { key: "patterns", labelKey: "projectData.tabPatterns" },
    ],
  },
  {
    labelKey: "projectData.groupHydrology",
    items: [
      { key: "climatology", labelKey: "projectData.tabClimatology" },
      { key: "snowpacks", labelKey: "projectData.tabSnowPacks" },
      { key: "aquifers", labelKey: "projectData.tabAquifers" },
      { key: "hydrographs", labelKey: "projectData.tabHydrographs" },
      { key: "lidcontrols", labelKey: "projectData.tabLidControls" },
    ],
  },
  {
    labelKey: "projectData.groupHydraulics",
    items: [
      { key: "transects", labelKey: "projectData.tabTransects" },
      { key: "streets", labelKey: "projectData.tabStreets" },
      { key: "inlets", labelKey: "projectData.tabInlets" },
    ],
  },
  {
    labelKey: "projectData.groupQuality",
    items: [
      { key: "pollutants", labelKey: "projectData.tabPollutants" },
      { key: "landuses", labelKey: "projectData.tabLandUses" },
    ],
  },
  {
    labelKey: "projectData.groupControl",
    items: [{ key: "controlrules", labelKey: "projectData.tabControlRules" }],
  },
];

/**
 * Toolbar dropdown listing every `DataTab` ProjectDataDialog.tsx knows about.
 * Picking one opens that section's own dialog directly (`onlyTab`), rather
 * than one big dialog with all of them behind an internal tab bar.
 */
export function ProjectDataMenu({
  open,
  onToggle,
  onClose,
  onSelectTab,
}: {
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
  onSelectTab: (tab: DataTab) => void;
}) {
  const t = useT();

  return (
    <DropdownMenu
      open={open}
      onToggle={onToggle}
      onClose={onClose}
      // See SelectionToolsMenu.tsx's own comment — same rail-trigger fix.
      align="left"
      triggerClassName="tool-rail-button dropdown-trigger"
      triggerLabel={t("projectMenu.projectData")}
      triggerIcon={<DataIcon />}
    >
      {GROUPS.map((group) => (
        <Fragment key={group.labelKey}>
          <span className="menu-popover-group-label">{t(group.labelKey)}</span>
          {group.items.map((item) => (
            <DropdownMenuItem
              key={item.key}
              icon={DATA_TAB_ICONS[item.key]}
              label={t(item.labelKey)}
              onClick={() => {
                onClose();
                onSelectTab(item.key);
              }}
            />
          ))}
        </Fragment>
      ))}
    </DropdownMenu>
  );
}
